import { z } from 'zod';

import { api } from '@/shared/api';
import { unwrap, unwrapCollection, type Page } from '@/shared/api/envelope';

import {
    apiSortFor,
    filterFor,
    isEveryStatus,
    lifecycleSlices,
    WITHIN_STATUS_SORT,
    type OrderQuery,
} from './filters';
import {
    toInspectionTypes,
    toOrder,
    toOrderDocument,
    toTimeline,
    type InspectionType,
    type Order,
    type OrderDocument,
    type OrderStatusCode,
    type TimelineEntry,
} from './model';
import {
    inspectionTypeResourceSchema,
    orderDocumentsEnvelopeSchema,
    orderResourceSchema,
    orderTimelineResourceSchema,
    startedSurveyEnvelopeSchema,
} from './types';

/** Schrijfacties gaan nooit in de offline cache van de http-client. */
const WRITE = { cacheKey: false } as const;

const DEFAULT_PER_PAGE = 25;

/** De API levert hoogstens 100 regels per pagina. */
const MAX_PER_PAGE = 100;

function filterQuery(query: OrderQuery, statuses: readonly OrderStatusCode[] | null) {
    return {
        'filter[status]': statuses && !isEveryStatus(statuses) ? statuses.join(',') : undefined,
        'filter[search]': query.search?.trim() || undefined,
        'filter[service]': query.service || undefined,
        'filter[assigned_user_uuid]': query.assigneeUuid || undefined,
    };
}

async function fetchPage(
    query: OrderQuery,
    statuses: readonly OrderStatusCode[] | null,
    sort: string,
    page: number,
    perPage: number,
): Promise<Page<Order>> {
    const result = unwrapCollection(
        orderResourceSchema,
        await api.get('/orders', {
            query: { ...filterQuery(query, statuses), sort, page, per_page: perPage },
        }),
    );

    return { items: result.items.map(toOrder), meta: result.meta };
}

async function countFor(query: OrderQuery, statuses: readonly OrderStatusCode[]): Promise<number> {
    const result = unwrapCollection(
        orderResourceSchema,
        await api.get('/orders', {
            query: { ...filterQuery(query, statuses), page: 1, per_page: 1 },
        }),
    );

    return result.meta?.total ?? result.items.length;
}

export interface NewOrder {
    clientUuid: string;
    propertyUuid: string;
    /**
     * De diensten van de keuring, in de volgorde waarin ze op de keuring komen. Eén
     * keuze kan meer dan één dienst aanzetten: een bouwtechnische keuring met de
     * NHG-bijlage is één keuze en twee diensten, elk met hun eigen tarief.
     */
    inspectionTypeUuids: readonly string[];
    assigneeUuid?: string | null;
    scheduledAt?: string | null;
    scheduledEndAt?: string | null;
    notes?: string | null;
}

export interface ScheduleInput {
    scheduledAt: string;
    scheduledEndAt: string | null;
    assigneeUuid: string | null;
}

/** Wat een gestarte keuring teruggeeft: de keuring zelf, en de opname om te openen. */
export interface StartedSurvey {
    order: Order;
    inspectionUuid: string;
}

/**
 * Keuringen lezen en wijzigen. Kantoor werkt online: er zit geen Dexie
 * tussen, alleen de GET-cache van de http-client. Elke schrijfactie geeft de
 * bijgewerkte keuring terug, inclusief nieuwe `allowed_transitions`.
 */
export const orderApi = {
    /**
     * `GET /orders` - de werklijst, gefilterd en gepagineerd door de API.
     *
     * "Sorteer: status" volgt de levenscyclus. De API sorteert `status` op het
     * alfabet, dus dan telt deze functie eerst per status (`per_page=1`) en haalt
     * daarna precies het stuk van elke status op dat op deze pagina valt.
     */
    async list(query: OrderQuery = {}): Promise<Page<Order>> {
        const statuses = filterFor(query.status ?? 'alle').statuses;
        const page = Math.max(1, query.page ?? 1);
        const perPage = Math.min(query.perPage ?? DEFAULT_PER_PAGE, MAX_PER_PAGE);
        const sort = apiSortFor(query.sort ?? 'opname');

        if (sort !== null) {
            return fetchPage(query, statuses, sort, page, perPage);
        }

        const totals = Object.fromEntries(
            await Promise.all(
                statuses.map(async (status) => [status, await countFor(query, [status])] as const),
            ),
        ) as Partial<Record<OrderStatusCode, number>>;
        const total = Object.values(totals).reduce((sum, count) => sum + (count ?? 0), 0);

        const parts = await Promise.all(
            lifecycleSlices(statuses, totals, page, perPage).map(async (slice) => {
                /* Een pagina van `perPage` die de gezochte plaatsen omvat, eventueel twee. */
                const first = Math.floor(slice.offset / perPage) + 1;
                const last = Math.floor((slice.offset + slice.count - 1) / perPage) + 1;
                const pages = await Promise.all(
                    Array.from({ length: last - first + 1 }, (_, index) =>
                        fetchPage(
                            query,
                            [slice.status],
                            WITHIN_STATUS_SORT,
                            first + index,
                            perPage,
                        ),
                    ),
                );
                const rows = pages.flatMap((result) => result.items);
                const skip = slice.offset - (first - 1) * perPage;

                return rows.slice(skip, skip + slice.count);
            }),
        );

        return {
            items: parts.flat(),
            meta: {
                current_page: page,
                per_page: perPage,
                total,
                last_page: Math.max(1, Math.ceil(total / perPage)),
            },
        };
    },

    /** Hoeveel keuringen er onder elke filterpil vallen, met dezelfde zoekterm en dienst. */
    async counts(query: OrderQuery, keys: readonly string[]): Promise<Record<string, number>> {
        const entries = await Promise.all(
            keys.map(async (key) => [key, await countFor(query, filterFor(key).statuses)] as const),
        );

        return Object.fromEntries(entries);
    },

    /**
     * Hoeveel keuringen deze omgeving heeft, zonder filter: `meta.total` van één
     * verzoek van één regel. `undefined` als de API geen telling gaf.
     */
    async total(): Promise<number | undefined> {
        return (await orderApi.counts({}, ['alle'])).alle;
    },

    /** Alle keuringen onder dit filter, voor de export. Pagina voor pagina. */
    async all(query: OrderQuery): Promise<Order[]> {
        const rows: Order[] = [];
        let page = 1;
        let lastPage = 1;

        do {
            const result = await orderApi.list({ ...query, page, perPage: MAX_PER_PAGE });

            rows.push(...result.items);
            lastPage = result.meta?.last_page ?? 1;
            page += 1;
        } while (page <= lastPage);

        return rows;
    },

    /** `GET /orders/{order}` */
    async show(orderUuid: string): Promise<Order> {
        return toOrder(unwrap(orderResourceSchema, await api.get(`/orders/${orderUuid}`)));
    },

    /** `GET /orders/{order}/timeline` - inclusief automatische stappen. */
    async timeline(orderUuid: string): Promise<TimelineEntry[]> {
        return toTimeline(
            unwrap(orderTimelineResourceSchema, await api.get(`/orders/${orderUuid}/timeline`)),
        );
    },

    /**
     * `GET /orders/{order}/documents` - rapportversies, facturen en bijlagen, met
     * kortlevende links. Niet in de offline cache: een verlopen link is erger dan
     * geen link.
     */
    async documents(orderUuid: string): Promise<OrderDocument[]> {
        return orderDocumentsEnvelopeSchema
            .parse(await api.get(`/orders/${orderUuid}/documents`, { cacheKey: false }))
            .data.map(toOrderDocument);
    },

    /**
     * `POST /orders` - een keuring openen. Dezelfde sleutel levert bij een
     * herhaling dezelfde keuring op, zonder tweede reservering. Te weinig
     * tegoed is **402** `insufficient-credits`.
     */
    async create(input: NewOrder, idempotencyKey: string): Promise<Order> {
        return toOrder(
            unwrap(
                orderResourceSchema,
                await api.post(
                    '/orders',
                    {
                        client_uuid: input.clientUuid,
                        property_uuid: input.propertyUuid,
                        services: input.inspectionTypeUuids.map((uuid) => ({
                            inspection_type_uuid: uuid,
                        })),
                        assigned_user_uuid: input.assigneeUuid ?? null,
                        scheduled_at: input.scheduledAt ?? null,
                        scheduled_end_at: input.scheduledEndAt ?? null,
                        notes: input.notes || null,
                        source: 'phone',
                    },
                    { ...WRITE, idempotencyKey },
                ),
            ),
        );
    },

    /**
     * `POST /orders/{order}/start` - de keuring nu beginnen: toewijzen, op dit moment
     * plannen, de status doorlopen en de opname openen. De klant krijgt er niets over;
     * een start is geen afspraak.
     *
     * `takeOver` neemt een keuring over van een collega, en kan alleen als die opname
     * nog niet gestart is. Een keuring die niet te starten is, is **409**
     * `order-not-startable` met een `reason` ({@link describeOrderProblem}).
     */
    async start(orderUuid: string, takeOver = false): Promise<StartedSurvey> {
        const started = startedSurveyEnvelopeSchema.parse(
            await api.post(
                `/orders/${orderUuid}/start`,
                takeOver ? { take_over: true } : {},
                WRITE,
            ),
        );

        return { order: toOrder(started.data), inspectionUuid: started.meta.inspection_uuid };
    },

    /** `POST /orders/{order}/schedule` - inspecteur en moment; verplaatsen is geen statuswijziging. */
    async schedule(orderUuid: string, input: ScheduleInput): Promise<Order> {
        return toOrder(
            unwrap(
                orderResourceSchema,
                await api.post(
                    `/orders/${orderUuid}/schedule`,
                    {
                        scheduled_at: input.scheduledAt,
                        scheduled_end_at: input.scheduledEndAt,
                        assigned_user_uuid: input.assigneeUuid,
                    },
                    WRITE,
                ),
            ),
        );
    },

    /** `POST /orders/{order}/status` - één stap uit `allowed_transitions`. */
    async transition(orderUuid: string, status: OrderStatusCode): Promise<Order> {
        return toOrder(
            unwrap(
                orderResourceSchema,
                await api.post(`/orders/${orderUuid}/status`, { status }, WRITE),
            ),
        );
    },

    /** `POST /orders/{order}/cancel` - met reden; het gereserveerde tegoed komt terug. */
    async cancel(orderUuid: string, reason: string): Promise<Order> {
        return toOrder(
            unwrap(
                orderResourceSchema,
                await api.post(`/orders/${orderUuid}/cancel`, { reason }, WRITE),
            ),
        );
    },

    /**
     * `POST /orders/{order}/address` - deze keuring op het object met het juiste adres
     * zetten. Andere keuringen op het oude adres blijven staan. Is het rapport al bij de
     * klant, dan **409** `order-address-locked`.
     */
    async changeAddress(orderUuid: string, propertyUuid: string): Promise<Order> {
        return toOrder(
            unwrap(
                orderResourceSchema,
                await api.post(
                    `/orders/${orderUuid}/address`,
                    { property_uuid: propertyUuid },
                    WRITE,
                ),
            ),
        );
    },

    /**
     * `POST /orders/{order}/services/{inspectionType}/complete` - een dienst die buiten
     * Smartkeur wordt gemaakt (een energielabel) klaar melden. Een opname of de NHG-bijlage is
     * **409** `order-service-not-completable`; nog eens klaar melden verandert niets.
     */
    async completeService(orderUuid: string, inspectionTypeUuid: string): Promise<Order> {
        return toOrder(
            unwrap(
                orderResourceSchema,
                await api.post(
                    `/orders/${orderUuid}/services/${inspectionTypeUuid}/complete`,
                    {},
                    WRITE,
                ),
            ),
        );
    },

    /**
     * `POST /invoices` met `order_uuid` - de conceptfactuur voor een afgeronde
     * keuring. Staat er al een, dan is het **409** `order-already-invoiced`.
     * De factuur zelf bekijk en verstuur je in Facturatie.
     */
    async invoice(orderUuid: string): Promise<{ invoiceUuid: string }> {
        const invoice = unwrap(
            z.object({ uuid: z.string() }),
            await api.post('/invoices', { order_uuid: orderUuid }, WRITE),
        );

        return { invoiceUuid: invoice.uuid };
    },

    /** `GET /inspection-types` - de diensten die dit bedrijf levert. */
    async inspectionTypes(): Promise<InspectionType[]> {
        return toInspectionTypes(
            unwrapCollection(inspectionTypeResourceSchema, await api.get('/inspection-types'))
                .items,
        );
    },
};
